/**
 * Triage System Types
 * 
 * Explicit triage state between intake and continued item work.
 * Part of Workstream 3: Triage System
 */

/**
 * Triage outcome - the decision made during triage
 */
export type TriageOutcome = 
  | 'pursue'              // Worth continued item work → TRIAGED/item work
  | 'defer'               // Not now, keep for future → DEFERRED
  | 'reject'              // Doesn't fit, discard → ARCHIVED
  | 'needs_clarification' // Can't decide, need more info → RAW with request
  | 'redirect';           // Belongs elsewhere → Different team/system

/**
 * Quick check questions for triage
 */
export interface TriageQuickChecks {
  /** Does this belong to our domain/scope? */
  inDomain: boolean;
  
  /** Can we understand what this is asking for? */
  understandable: boolean;
  
  /** Is there something we could actually do about this? */
  actionable: boolean;
  
  /** Have we verified this isn't a duplicate? */
  notDuplicate: boolean;
  
  /** Do we have context about who submitted and why? */
  hasSubmitterContext: boolean;
}

/**
 * Triage assessment - the full triage decision record
 */
export interface TriageAssessment {
  id: string;
  itemId: string;
  
  // Who and when
  triagedBy: string;
  triagedAt: Date;
  
  // Quick checks
  quickChecks: TriageQuickChecks;
  
  // Decision
  outcome: TriageOutcome;
  
  // Rationale (required for defer/reject)
  rationale?: string;
  
  // For redirect outcome
  redirectTarget?: string;
  redirectReason?: string;
  
  // For needs_clarification outcome
  clarificationRequest?: string;
  clarificationRequestedFrom?: string;
  
  // Related items (for duplicates/similar)
  relatedItemIds?: string[];
  
  // Bucket assignment (for routing)
  bucketId?: string;
  
  // Confidence in decision
  confidence?: 'high' | 'medium' | 'low';
  
  // Notes
  notes?: string;
  
  // Workspace scoping
  workspaceId?: string;
}

/**
 * Triage queue item - enriched view for the queue
 */
export interface TriageQueueItem {
  id: string;
  title: string;
  description?: string;
  
  // Submission info
  submittedBy: string;
  submittedAt: Date;
  source?: string;
  
  // Time in queue
  daysInQueue: number;
  
  // Similar items detected
  similarItems: Array<{
    id: string;
    title: string;
    similarity: number;
  }>;
  
  // Previous triage (if re-triaging)
  previousAssessment?: TriageAssessment;
  
  // Suggested bucket based on content
  suggestedBucket?: string;
  
  // Priority indicators
  hasUrgencySignal: boolean;
  hasExternalSource: boolean;
  hasStakeholderMention: boolean;
}

/**
 * Triage statistics
 */
export interface TriageStats {
  // Queue stats
  totalInQueue: number;
  averageDaysInQueue: number;
  oldestItemDays: number;
  
  // Outcome distribution (last 30 days)
  byOutcome: Record<TriageOutcome, number>;
  
  // Throughput
  triagedToday: number;
  triagedThisWeek: number;
  triagedThisMonth: number;
  
  // Quality indicators
  clarificationRequestRate: number;
  redirectRate: number;
  
  // By triager
  byTriager: Array<{
    userId: string;
    count: number;
    avgTimeMinutes: number;
  }>;
}

/**
 * Batch triage input
 */
export interface BatchTriageInput {
  itemIds: string[];
  outcome: TriageOutcome;
  rationale: string;
  bucketId?: string;
}

/**
 * Triage configuration
 */
export interface TriageConfig {
  // Is triage required before continued item work?
  requireTriageBeforeShaping: boolean;
  
  // Auto-assign rules
  autoAssignRules: TriageAutoAssignRule[];
  
  // SLA settings
  maxDaysInQueue: number;
  escalateAfterDays: number;
  
  // Default triagers by bucket
  defaultTriagers: Record<string, string[]>;
}

/**
 * Auto-assign rule for triage routing
 */
export interface TriageAutoAssignRule {
  id: string;
  name: string;
  
  // Conditions
  conditions: {
    sourceContains?: string[];
    titleContains?: string[];
    submitterDomain?: string[];
  };
  
  // Actions
  assignToBucket: string;
  assignToTriager?: string;
  priority?: 'high' | 'normal' | 'low';
}

/**
 * Triage bucket for routing
 */
export interface TriageBucket {
  id: string;
  name: string;
  description?: string;
  
  // Owners
  ownerIds: string[];
  
  // Settings
  defaultOutcome?: TriageOutcome;
  requireRationale: boolean;
  
  // Stats
  itemCount: number;
  avgTriageTimeMinutes: number;
}

// Helper functions

/**
 * Get display label for triage outcome
 */
export function getTriageOutcomeLabel(outcome: TriageOutcome): string {
  const labels: Record<TriageOutcome, string> = {
    pursue: 'Pursue',
    defer: 'Defer',
    reject: 'Reject',
    needs_clarification: 'Needs Clarification',
    redirect: 'Redirect',
  };
  return labels[outcome];
}

/**
 * Get color for triage outcome
 */
export function getTriageOutcomeColor(outcome: TriageOutcome): string {
  const colors: Record<TriageOutcome, string> = {
    pursue: 'green',
    defer: 'yellow',
    reject: 'red',
    needs_clarification: 'orange',
    redirect: 'blue',
  };
  return colors[outcome];
}

/**
 * Get icon for triage outcome
 */
export function getTriageOutcomeIcon(outcome: TriageOutcome): string {
  const icons: Record<TriageOutcome, string> = {
    pursue: '✅',
    defer: '⏸️',
    reject: '❌',
    needs_clarification: '❓',
    redirect: '↗️',
  };
  return icons[outcome];
}

/**
 * Check if all quick checks are complete
 */
export function areQuickChecksComplete(checks: Partial<TriageQuickChecks>): boolean {
  return (
    checks.inDomain !== undefined &&
    checks.understandable !== undefined &&
    checks.actionable !== undefined &&
    checks.notDuplicate !== undefined &&
    checks.hasSubmitterContext !== undefined
  );
}

/**
 * Check if quick checks suggest pursuing
 */
export function quickChecksSuggestPursue(checks: TriageQuickChecks): boolean {
  return (
    checks.inDomain &&
    checks.understandable &&
    checks.actionable &&
    checks.notDuplicate
  );
}

/**
 * Calculate quick check score (0-5)
 */
export function calculateQuickCheckScore(checks: TriageQuickChecks): number {
  let score = 0;
  if (checks.inDomain) score++;
  if (checks.understandable) score++;
  if (checks.actionable) score++;
  if (checks.notDuplicate) score++;
  if (checks.hasSubmitterContext) score++;
  return score;
}

/**
 * Create empty quick checks
 */
export function createEmptyQuickChecks(): TriageQuickChecks {
  return {
    inDomain: false,
    understandable: false,
    actionable: false,
    notDuplicate: false,
    hasSubmitterContext: false,
  };
}

/**
 * Validate triage assessment completeness
 */
export function validateTriageAssessment(assessment: Partial<TriageAssessment>): string[] {
  const errors: string[] = [];
  
  if (!assessment.outcome) {
    errors.push('Outcome is required');
  }
  
  if (!assessment.quickChecks || !areQuickChecksComplete(assessment.quickChecks)) {
    errors.push('All quick checks must be completed');
  }
  
  if (assessment.outcome === 'defer' || assessment.outcome === 'reject') {
    if (!assessment.rationale) {
      errors.push('Rationale is required for defer/reject');
    }
  }
  
  if (assessment.outcome === 'redirect') {
    if (!assessment.redirectTarget) {
      errors.push('Redirect target is required');
    }
  }
  
  if (assessment.outcome === 'needs_clarification') {
    if (!assessment.clarificationRequest) {
      errors.push('Clarification request is required');
    }
  }
  
  return errors;
}
